// Deposits on sold estimates in the office (BL-006 PR 2), kept apart from the screens so they are
// tested: a deposit in words, the deposit suggested for an estimate, and the take-a-deposit form.

import { parseMoney } from '../books/model.ts';
import type { Estimate } from './job-model.ts';

/** deposits.list's row. */
export interface Deposit {
  creditId: string;
  paymentId: string | null;
  estimateId: string;
  /** Absent from servers before QA-794. */
  estimateNumber?: string;
  customerId: string;
  amountCents: number;
  remainingCents: number;
  state: 'held' | 'reserved' | 'applied' | 'refunded';
  reservedInvoiceId: string | null;
  appliedCents: number;
  refundedCents: number;
  method: string | null;
  receivedAt: string | null;
}

const cad = (cents: number) =>
  new Intl.NumberFormat('en-CA', { style: 'currency', currency: 'CAD' }).format(cents / 100);

/** One deposit in words: "$500.00 deposit, held for the estimate", "… applied to the invoice". */
export function depositText(d: Deposit): string {
  const what = `${cad(d.amountCents)} deposit`;
  if (d.state === 'reserved') return `${what}, waiting for this work’s invoice`;
  if (d.state === 'held') {
    const used = d.amountCents - d.remainingCents;
    return used > 0 ? `${what}, ${cad(d.remainingCents)} still on account` : `${what}, held for the estimate`;
  }
  if (d.state === 'refunded') return `${what}, refunded`;
  return `${what}, applied to the invoice`;
}

/** What is still the estimate's: left on its deposits and what they paid on its work. */
export const heldCents = (ds: readonly Deposit[]) =>
  ds.reduce((s, d) => s + d.remainingCents + d.appliedCents, 0);

/** The deposit suggested for a signed total (estimates.depositPercent), less what is held. */
export function suggestedCents(totalCents: number, percent: number, held: number): number {
  const left = Math.max(0, totalCents - held);
  return Math.min(left, Math.max(0, Math.round((totalCents * percent) / 100) - held));
}

export type DepositMethod = 'cash' | 'cheque' | 'etransfer';
export const DEPOSIT_METHODS: { value: DepositMethod; label: string }[] = [
  { value: 'cheque', label: 'Cheque' },
  { value: 'etransfer', label: 'E-transfer' },
  { value: 'cash', label: 'Cash' },
];

/** The form's amount, checked: what payments.record takes, or what's wrong in words. */
export function depositAmount(text: string, leftCents: number): { cents: number } | { problem: string } {
  const cents = parseMoney(text);
  if (cents === null || cents <= 0) return { problem: 'An amount like 500.00.' };
  if (cents > leftCents) return { problem: `At most ${cad(leftCents)}: what’s left of the signed total.` };
  return { cents };
}

/** "Card deposits are taken on the terminal or in the portal" etc.: the line under the form. */
export const DEPOSIT_NOTE =
  'It’s held for this estimate and taken off the invoice when the work is done. Card deposits are taken on the iPad’s terminal or in the customer’s portal.';

/** An estimate signed for later (sold, or signed and waiting for the office's OK): it takes deposits. */
export const signedForLater = (e: Pick<Estimate, 'status' | 'disposition' | 'waitingSale'>) =>
  (e.status === 'sold' && e.disposition === 'sold_later') || !!e.waitingSale;

/** The signed option's total, with tax. */
export function signedTotal(e: Pick<Estimate, 'selectedOptionId' | 'waitingSale' | 'options'>): number {
  const id = e.selectedOptionId ?? e.waitingSale?.optionId;
  return e.options.find((o) => o.id === id)?.totalCents ?? 0;
}

/**
 * What an invoice says of the deposits it doesn't show as paid yet (QA-794): one waiting for it is
 * taken off when the job is completed; one held for an estimate on its job, not yet booked, comes
 * off the invoice its work is booked on. Applied ones are already among its money in.
 */
export function invoiceDepositNotes(deposits: readonly Deposit[], invoiceId: string): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const d of deposits) {
    if (seen.has(d.creditId) || d.remainingCents <= 0) continue;
    seen.add(d.creditId);
    const on = d.estimateNumber ? ` on estimate ${d.estimateNumber}` : '';
    if (d.state === 'reserved' && d.reservedInvoiceId === invoiceId)
      out.push(
        `A ${cad(d.remainingCents)} deposit${on} waits for this invoice: it’s taken off when the job is completed.`,
      );
    else if (d.state === 'held')
      out.push(
        `A ${cad(d.remainingCents)} deposit${on} is held for that work: it comes off the invoice the work is booked on.`,
      );
  }
  return out;
}
